import { Component, Injector, OnDestroy } from '@angular/core';
import {
    ClassNames,
    Color,
    DateFormat,
    DownloadService,
    FeatureFlagService,
    Serializer,
    Size
} from '@lsq-frontend/common-ui';
import { getRouterLinkUrlById, Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { PopoverPlacement } from 'ngx-smart-popover';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import * as copy from '../../../constants/copy.constants';
import {
    AccountOverviewByDate,
    ExpenseFeeBreakdown,
    GetAccountOverviewByDateRequest,
    GetAccountOverviewByDateResponse,
    GetAccountOverviewByDateService,
    GetFundsOutstandingFeeStatementRequest,
    GetFundsOutstandingFeeStatementService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    atlas,
    REPORTS_COLLECTIONS,
    REPORTS_PURCHASES
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { DateRangeReportComponent } from '../base/date-range-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

@Component({
    selector: 'account-overview-by-date',
    template: `
        <div [ngClass]="{ 'min-page-width-large-up': !(dataSource?.loading$ | async) }">
            <div>
                <lsq-banner-search></lsq-banner-search>
                <lsq-banner
                    [titleText]="title"
                    [reportName]="title"
                    [analyticsName]="analyticsName"
                    [service]="apiService"
                    [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                    [hideExport]="dataSource.noData$ | async"
                    [hideDatePicker]="!(dataSource.data$ | async)"
                    [datePickerConfiguration]="datePickerConfiguration"
                >
                </lsq-banner>
            </div>
            <div class="container-fluid page-container">
                <div class="row">
                    <div class="col">
                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="mtc-header-content">
                                    <div class="mtc-header-title-only">
                                        {{ row.date | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>
                            </ng-template>

                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ purchasesHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.PURCHASE_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        <a
                                            *ngIf="row.purchases"
                                            [ngStyle]="{ 'font-size': '1.3rem' }"
                                            [routerLink]="purchasesRouterLinkUrl"
                                            [queryParams]="{ date: row.date }"
                                        >
                                            {{ row.purchases | lsqCurrency }}
                                        </a>
                                        <span *ngIf="!row.purchases">{{
                                            row.purchases | lsqCurrency
                                        }}</span>
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ collectionsHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.COLLECTIONS_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        <a
                                            *ngIf="row.hasCollectionActivity"
                                            [ngStyle]="{ 'font-size': '1.3rem' }"
                                            [routerLink]="collectionsRouterLinkUrl"
                                            [queryParams]="{ date: row.date }"
                                        >
                                            {{ row.collections | lsqCurrency }}
                                        </a>
                                        <span *ngIf="!row.hasCollectionActivity">{{
                                            row.collections | lsqCurrency
                                        }}</span>
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ chargeBacksHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.CHARGEBACKS_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.chargebacks | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ nonFactoredHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.NON_FACTORED_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.nonFactored | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ fundingHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.FUNDING_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.funding | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ feesHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.FEE_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        <a
                                            *ngIf="
                                                displayFundsOutstandingStatementLink(
                                                    row.feeBreakdown
                                                )
                                            "
                                            (click)="getFundsOutStandingFeeStatement(row.date)"
                                        >
                                            <span>{{ row.feesIncurred | currencyDash }}</span>
                                        </a>
                                        <span
                                            *ngIf="
                                                !displayFundsOutstandingStatementLink(
                                                    row.feeBreakdown
                                                )
                                            "
                                        >
                                            {{ row.feesIncurred | currencyDash }}</span
                                        >
                                        <popover-content #feePopover [size]="size.MediumSmall">
                                            <table class="expense-popover">
                                                <tbody>
                                                    <tr
                                                        *ngFor="
                                                            let fee of row.feeBreakdown.expensesList
                                                        "
                                                    >
                                                        <td>{{ fee.category }}</td>
                                                        <td>
                                                            <div>
                                                                <lsq-icon
                                                                    [type]="icons.Add"
                                                                    [color]="color.Black"
                                                                ></lsq-icon>
                                                                {{ fee.amount | currencyDash }}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>{{ feesHeader }}</td>
                                                        <td>
                                                            {{
                                                                row.feeBreakdown.totalExpenses
                                                                    | currencyDash
                                                            }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </popover-content>
                                        <lsq-icon
                                            *ngIf="displayFeePopover(row.feeBreakdown)"
                                            [type]="icons.Info"
                                            [color]="color.Gray"
                                            [ngClass]="[
                                                classNames.TableCellPopoverIcon,
                                                classNames.PopoverIcon
                                            ]"
                                            [popover]="feePopover"
                                            [popoverPlacement]="placement.BottomLeft"
                                            [popoverSize]="size.MediumSmall"
                                        >
                                        </lsq-icon>
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ expensesHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.EXPENSES_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        <span>{{ row.expenses | currencyDash }}</span>
                                        <popover-content #expensePopover [size]="size.MediumSmall">
                                            <table class="expense-popover">
                                                <tbody>
                                                    <tr
                                                        *ngFor="
                                                            let fee of row.expenseBreakdown
                                                                .expensesList
                                                        "
                                                    >
                                                        <td>{{ fee.category }}</td>
                                                        <td>
                                                            <div>
                                                                <lsq-icon
                                                                    [type]="icons.Add"
                                                                    [color]="color.Black"
                                                                ></lsq-icon>
                                                                {{ fee.amount | currencyDash }}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>{{ expensesHeader }}</td>
                                                        <td>
                                                            {{
                                                                row.expenseBreakdown.totalExpenses
                                                                    | currencyDash
                                                            }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </popover-content>
                                        <lsq-icon
                                            *ngIf="displayExpensesPopover(row.expenseBreakdown)"
                                            [type]="icons.Info"
                                            [color]="color.Gray"
                                            [ngClass]="[
                                                classNames.TableCellPopoverIcon,
                                                classNames.PopoverIcon
                                            ]"
                                            [popover]="expensePopover"
                                            [popoverPlacement]="placement.BottomLeft"
                                        >
                                        </lsq-icon>
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ ineligiblesHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.INELIGIBLES_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.ineligibles | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ unpaidInvoicesHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.AR_BALANCE_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.arBalance | lsqCurrency }}
                                    </div>
                                </div>

                                <div
                                    class="card-item"
                                    *ngIf="(dataSource.data$ | async)?.totalTransferAmount"
                                >
                                    <div class="card-item-label">
                                        {{ transfersHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.TRANSFERS_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.transferAmount | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ fundsOutstandingHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.FUNDS_OUTSTANDING_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.fundsOutstanding | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ totalReservesHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.TOTAL_RESERVES_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.totalReserves | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ availableFundsHeader }}
                                        <lsq-icon-popover
                                            [text]="copy.AVAILABLE_FUNDS_DESCRIPTION"
                                        ></lsq-icon-popover>
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.availableFunds | lsqCurrency }}
                                    </div>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>

                        <lsq-data-table
                            *ngIf="!(responsiveService.smBelow | async)"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [hasFooter]="true"
                            [hasStickyHeader]="true"
                            [columnVisibilities]="{
                                transferAmount: (dataSource.data$ | async)?.hasTransfers
                            }"
                            (takeChanged)="handleTakeChange($event)"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-date-column
                                [column]="{
                                    header: dateHeader,
                                    key: 'date',
                                    className: 'grand-total'
                                }"
                            >
                                <ng-template [custom-footer-cell] let-row="row">
                                    <div class="grand-total">
                                        <span i18n="Total label for table">Grand Total</span>
                                    </div>
                                </ng-template>
                            </lsq-date-column>

                            <lsq-column
                                [column]="{
                                    header: purchasesHeader,
                                    key: 'purchases',
                                    headerPopoverContent: copy.PURCHASE_DESCRIPTION,
                                    className:
                                        classNames.AlignRight +
                                        ' ' +
                                        classNames.ColumnCurrency +
                                        ' purchases-col'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        *ngIf="row.purchases"
                                        [routerLink]="purchasesRouterLinkUrl"
                                        [queryParams]="{ date: row.date }"
                                    >
                                        {{ row.purchases | lsqCurrency }}
                                    </a>
                                    <span *ngIf="!row.purchases">{{
                                        row.purchases | lsqCurrency
                                    }}</span>
                                </ng-template>
                                <ng-template [custom-footer-cell] let-row="row">
                                    {{ (dataSource.data$ | async)?.totalPurchases | lsqCurrency }}
                                </ng-template>
                            </lsq-column>

                            <lsq-column
                                [column]="{
                                    header: collectionsHeader,
                                    key: 'collections',
                                    headerPopoverContent: copy.COLLECTIONS_DESCRIPTION,
                                    className:
                                        classNames.AlignRight +
                                        ' ' +
                                        classNames.ColumnCurrency +
                                        ' collections-col'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        *ngIf="row.hasCollectionActivity"
                                        [routerLink]="collectionsRouterLinkUrl"
                                        [queryParams]="{ date: row.date }"
                                    >
                                        {{ row.collections | lsqCurrency }}
                                    </a>
                                    <span *ngIf="!row.hasCollectionActivity">{{
                                        row.collections | lsqCurrency
                                    }}</span>
                                </ng-template>
                                <ng-template [custom-footer-cell] let-row="row">
                                    {{ (dataSource.data$ | async)?.totalCollections | lsqCurrency }}
                                </ng-template>
                            </lsq-column>

                            <lsq-currency-column
                                [column]="{
                                    header: chargeBacksHeader,
                                    key: 'chargebacks',
                                    headerPopoverContent: copy.CHARGEBACKS_DESCRIPTION,
                                    className: 'charge-backs-col'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalChargebacks"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: nonFactoredHeader,
                                    key: 'nonFactored',
                                    headerPopoverContent: copy.NON_FACTORED_DESCRIPTION,
                                    className: 'non-factored-col'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalNonFactored"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: fundingHeader,
                                    key: 'funding',
                                    headerPopoverContent: copy.FUNDING_DESCRIPTION,
                                    className: 'funding-col'
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalFunding"
                            >
                            </lsq-currency-column>

                            <lsq-fee-popover-column
                                [data$]="dataSource.data$"
                            ></lsq-fee-popover-column>

                            <lsq-expenses-popover-column
                                [data$]="dataSource.data$"
                            ></lsq-expenses-popover-column>

                            <lsq-currency-column
                                [column]="{
                                    header: ineligiblesHeader,
                                    key: 'ineligibles',
                                    headerPopoverContent: copy.INELIGIBLES_DESCRIPTION,
                                    className: 'ineligibles-col'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: unpaidInvoicesHeader,
                                    key: 'arBalance',
                                    headerPopoverContent: copy.AR_BALANCE_DESCRIPTION,
                                    className: 'unpaid-invoices-col'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: transfersHeader,
                                    key: 'transferAmount',
                                    headerPopoverContent: copy.TRANSFERS_DESCRIPTION,
                                    className: 'transfers-col'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: fundsOutstandingHeader,
                                    key: 'fundsOutstanding',
                                    headerPopoverContent: copy.FUNDS_OUTSTANDING_DESCRIPTION,
                                    className: 'funds-outstanding-col'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: totalReservesHeader,
                                    key: 'totalReserves',
                                    headerPopoverContent: copy.TOTAL_RESERVES_DESCRIPTION,
                                    className: 'total-reserves-col'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: availableFundsHeader,
                                    key: 'availableFunds',
                                    headerPopoverContent: copy.AVAILABLE_FUNDS_DESCRIPTION,
                                    className: 'available-funds-col'
                                }"
                            >
                            </lsq-currency-column>
                        </lsq-data-table>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class AccountOverviewByDateComponent extends DateRangeReportComponent implements OnDestroy {
    public title = 'Overview by Date';

    public analyticsName = AnalyticsValue.AccountOverviewDate;
    public userStateType = ReportsUserStateTypes.GetAccountOverviewByDateRequest;
    public savedDateFilterName = null;

    // Constants.
    public classNames = ClassNames;
    public color = Color;
    public copy = copy;
    public dateFormat = DateFormat;
    public icons = Icons;
    public placement = PopoverPlacement;
    public size = Size;
    public purchasesRouterLinkUrl = getRouterLinkUrlById(atlas, REPORTS_PURCHASES);
    public collectionsRouterLinkUrl = getRouterLinkUrlById(atlas, REPORTS_COLLECTIONS);

    public availableFundsHeader = this._i18n({
        value: 'Available Funds',
        meaning: 'Available Funds header label'
    });
    public totalReservesHeader = this._i18n({
        value: 'Total Reserves',
        meaning: 'Total Reserves header label'
    });
    public fundsOutstandingHeader = this._i18n({
        value: 'Funds Outstanding',
        meaning: 'Funds Outstanding header label'
    });
    public transfersHeader = this._i18n({
        value: 'Transfers',
        meaning: 'Transfers header label'
    });
    public unpaidInvoicesHeader = this._i18n({
        value: 'Unpaid Invoices',
        meaning: 'Unpaid Invoices header label'
    });
    public ineligiblesHeader = this._i18n({
        value: 'Ineligibles',
        meaning: 'Ineligibles header label'
    });
    public fundingHeader = this._i18n({
        value: 'Funding',
        meaning: 'Fundings header label'
    });
    public nonFactoredHeader = this._i18n({
        value: 'Non-Factored',
        meaning: 'Non-Factored header label'
    });
    public chargeBacksHeader = this._i18n({
        value: 'Charge-Backs',
        meaning: 'Charge-Backs header label'
    });
    public collectionsHeader = this._i18n({
        value: 'Collections',
        meaning: 'Collections header label'
    });
    public purchasesHeader = this._i18n({
        value: 'Purchases',
        meaning: 'Purchases header label'
    });
    public dateHeader = this._i18n({
        value: 'Date',
        meaning: 'Date header label'
    });
    public expensesHeader = this._i18n({
        value: 'Expenses',
        meaning: 'Expenses header label'
    });
    public feesHeader = this._i18n({
        value: 'Fees',
        meaning: 'Fees header label'
    });

    public dataSource = new LsqApiServiceListSource<
        GetAccountOverviewByDateResponse,
        GetAccountOverviewByDateRequest,
        AccountOverviewByDate
    >(this.apiService);

    public companyKey;

    constructor(
        private _i18n: I18n,
        public apiService: GetAccountOverviewByDateService,
        public getFundsOutstandingFeeStatementService: GetFundsOutstandingFeeStatementService,
        public responsiveService: ResponsiveService,
        public injector: Injector,
        private _userService: UserService,
        private _downloadService: DownloadService,
        private _featureFlagService: FeatureFlagService
    ) {
        super(injector);

        void this.dispatchInitialRequest();

        this.companyKey = this._userService.getCompanyKey();
    }

    public ngOnDestroy(): void {}

    public displayFeePopover(feeBreakdown: ExpenseFeeBreakdown): boolean {
        return feeBreakdown.expensesList && feeBreakdown.expensesList.length > 0;
    }

    public displayFundsOutstandingStatementLink(feeBreakdown: ExpenseFeeBreakdown): boolean {
        let response = false;
        if (this._featureFlagService.isOnSync(FeatureFlags.FundsOutstandingFeeStatement)) {
            feeBreakdown.expensesList.forEach((x) => {
                if (x.category === 'Funds Usage Daily Fee') {
                    response = true;
                }
            });
        }
        return response;
    }

    public displayExpensesPopover(expenseBreakdown: ExpenseFeeBreakdown): boolean {
        return (
            expenseBreakdown.totalExpenses > 0 &&
            expenseBreakdown.expensesList &&
            expenseBreakdown.expensesList.length > 0
        );
    }

    public getFundsOutStandingFeeStatement(date): void {
        const request = new GetFundsOutstandingFeeStatementRequest({
            asOfDateFilter: date,
            companyKey: this.companyKey
        });

        this._downloadService.execute(
            this.getFundsOutstandingFeeStatementService,
            request,
            FileType.Csv,
            `Funds Outstanding Daily Fee Statement ${date}`
        );
    }
}
